import React from 'react';
import {
  Box, Typography, Select, MenuItem, TextField, Paper, IconButton,
  Tooltip, Button
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

/**
 * Visual condition builder — shared by the section-spec filter editor and the
 * v6 tree step drawer. Renders engine condition nodes:
 *   leaf:  { lhs: operand, cmp, rhs: operand | [lo, hi] | n, skip_reason? }
 *   group: { op: 'all'|'any'|'not', conditions: [node…] }
 *   operand: {indicator,offset?} | {param|input} | {context} | {value}
 *            | {arith:{op,operands[]}}
 *
 * `refKey` selects the ref operand syntax written when the user picks a
 * reference: 'param' for section specs ({param:'x'}), 'input' for tree specs
 * ({input:'x'}). Both read via the same dropdown of known.paramNames.
 */

const CMP_OPTIONS = [
  { v: '<', label: '< less than' }, { v: '<=', label: '≤ at most' },
  { v: '>', label: '> greater than' }, { v: '>=', label: '≥ at least' },
  { v: '==', label: '= equals' }, { v: '!=', label: '≠ not equal' },
  { v: 'between', label: 'between (inclusive)' },
  { v: 'crosses_above', label: 'crosses above' },
  { v: 'crosses_below', label: 'crosses below' },
  { v: 'rising', label: 'rising for N bars' },
  { v: 'falling', label: 'falling for N bars' },
];
const GROUP_OPS = [{ v: 'all', label: 'AND — all pass' }, { v: 'any', label: 'OR — any passes' }, { v: 'not', label: 'NOT — invert first' }];
const PRICE_FIELDS = ['open', 'high', 'low', 'close', 'volume'];

function operandKind(o) {
  if (!o || typeof o !== 'object') return 'literal';
  if (o.arith) return 'arith';
  if (o.indicator !== undefined) return 'indicator';
  if (o.param !== undefined || o.input !== undefined) return 'param';
  if (o.context !== undefined) return 'context';
  return 'literal';
}

function OperandEditor({ value, onChange, known, depth = 0, refKey = 'param' }) {
  const kind = operandKind(value);
  const setKind = (k) => {
    const next = { indicator: { indicator: known.indicatorIds[0] || '', offset: -1 },
      param: { [refKey]: known.paramNames[0] || '' }, context: { context: known.contextValues[0] || 'vix_open' },
      literal: { value: 0 },
      arith: { arith: { op: '-', operands: [{ value: 0 }, { value: 0 }] } } }[k];
    onChange(next);
  };
  return (
    <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
      <Select size="small" value={kind} onChange={e => setKind(e.target.value)} sx={{ minWidth: 90 }}>
        <MenuItem value="indicator">Indicator</MenuItem>
        <MenuItem value="param">{refKey === 'input' ? 'Input' : 'Parameter'}</MenuItem>
        <MenuItem value="context">Context</MenuItem>
        <MenuItem value="literal">Literal</MenuItem>
        {depth < 1 && <MenuItem value="arith">Formula</MenuItem>}
      </Select>
      {kind === 'indicator' && (
        <>
          <Select size="small" value={value.indicator || ''} sx={{ minWidth: 110 }}
            onChange={e => onChange({ ...value, indicator: e.target.value })}>
            {[...known.indicatorIds, ...PRICE_FIELDS].map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
          </Select>
          <TextField size="small" label="bar off" type="number" value={value.offset ?? 0}
            sx={{ width: 80 }} inputProps={{ min: -30, max: 0 }}
            onChange={e => onChange({ ...value, offset: +e.target.value || 0 })} />
        </>
      )}
      {kind === 'param' && (
        <Select size="small" value={value.param ?? value.input ?? ''} sx={{ minWidth: 130 }}
          onChange={e => onChange({ [refKey]: e.target.value })}>
          {known.paramNames.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </Select>
      )}
      {kind === 'context' && (
        <Select size="small" value={value.context || ''} sx={{ minWidth: 110 }}
          onChange={e => onChange({ context: e.target.value })}>
          {known.contextValues.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </Select>
      )}
      {kind === 'literal' && (
        <TextField size="small" type="number" value={value?.value ?? value ?? 0} sx={{ width: 100 }}
          onChange={e => onChange({ value: +e.target.value })} />
      )}
      {kind === 'arith' && (
        <Box sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center', border: '1px dashed #888', p: 0.5, borderRadius: 1 }}>
          <OperandEditor value={value.arith.operands[0]} depth={depth + 1} known={known} refKey={refKey}
            onChange={o => onChange({ arith: { ...value.arith, operands: [o, value.arith.operands[1]] } })} />
          <Select size="small" value={value.arith.op} sx={{ width: 60 }}
            onChange={e => onChange({ arith: { ...value.arith, op: e.target.value } })}>
            {['+', '-', '*', '/', 'abs', 'neg'].map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </Select>
          {!['abs', 'neg'].includes(value.arith.op) && (
            <OperandEditor value={value.arith.operands[1]} depth={depth + 1} known={known} refKey={refKey}
              onChange={o => onChange({ arith: { ...value.arith, operands: [value.arith.operands[0], o] } })} />
          )}
        </Box>
      )}
    </Box>
  );
}

// ── Condition tree editor (recursive) ───────────────────────────────────────
function LeafEditor({ node, onChange, onRemove, onToGroup, known, refKey }) {
  const cmp = node.cmp || '<=';
  const rhsIsPair = cmp === 'between';
  const rhsIsN = cmp === 'rising' || cmp === 'falling';
  return (
    <Paper variant="outlined" sx={{ p: 1, mb: 1, display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
      <OperandEditor value={node.lhs} known={known} refKey={refKey} onChange={v => onChange({ ...node, lhs: v })} />
      <Select size="small" value={cmp} sx={{ minWidth: 150 }}
        onChange={e => {
          const c = e.target.value;
          const rhs = c === 'between' ? [{ value: 0 }, { value: 0 }]
            : (c === 'rising' || c === 'falling') ? { value: 2 } : { value: 0 };
          onChange({ ...node, cmp: c, rhs });
        }}>
        {CMP_OPTIONS.map(o => <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>)}
      </Select>
      {rhsIsPair && (
        <>
          <OperandEditor value={node.rhs?.[0]} known={known} refKey={refKey}
            onChange={v => onChange({ ...node, rhs: [v, node.rhs?.[1]] })} />
          <Typography variant="caption">and</Typography>
          <OperandEditor value={node.rhs?.[1]} known={known} refKey={refKey}
            onChange={v => onChange({ ...node, rhs: [node.rhs?.[0], v] })} />
        </>
      )}
      {rhsIsN && (
        <TextField size="small" type="number" label="bars" sx={{ width: 80 }}
          value={node.rhs?.value ?? node.rhs ?? 2} inputProps={{ min: 1, max: 60 }}
          onChange={e => onChange({ ...node, rhs: { value: +e.target.value } })} />
      )}
      {!rhsIsPair && !rhsIsN && (
        <OperandEditor value={node.rhs} known={known} refKey={refKey} onChange={v => onChange({ ...node, rhs: v })} />
      )}
      <TextField size="small" label="skip reason" sx={{ width: 170 }}
        value={node.skip_reason || ''} placeholder="REASON_CODE"
        onChange={e => onChange({ ...node, skip_reason: e.target.value || undefined })} />
      <Tooltip title="Wrap in group"><IconButton size="small" onClick={onToGroup}><AddIcon fontSize="small" /></IconButton></Tooltip>
      <IconButton size="small" onClick={onRemove}><DeleteIcon fontSize="small" /></IconButton>
    </Paper>
  );
}

function NodeEditor({ node, onChange, onRemove, known, depth = 0, refKey = 'param' }) {
  if (node.op) {
    const kids = node.conditions || [];
    const setKid = (i, c) => { const n = [...kids]; n[i] = c; onChange({ ...node, conditions: n }); };
    const delKid = (i) => onChange({ ...node, conditions: kids.filter((_, j) => j !== i) });
    return (
      <Paper variant="outlined" sx={{ p: 1, mb: 1, ml: depth ? 2 : 0, borderColor: 'primary.main', borderLeftWidth: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
          <Select size="small" value={node.op} sx={{ minWidth: 160 }}
            onChange={e => onChange({ ...node, op: e.target.value })}>
            {GROUP_OPS.map(o => <MenuItem key={o.v} value={o.v}>{o.label}</MenuItem>)}
          </Select>
          <Button size="small" startIcon={<AddIcon />} variant="outlined"
            onClick={() => onChange({ ...node, conditions: [...kids, { lhs: { context: 'vix_open' }, cmp: '<=', rhs: { value: 0 } }] })}>
            Condition</Button>
          {node.op !== 'not' && (
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => onChange({ ...node, conditions: [...kids, { op: 'all', conditions: [] }] })}>
              Group</Button>
          )}
          {onRemove && <IconButton size="small" onClick={onRemove}><DeleteIcon fontSize="small" /></IconButton>}
        </Box>
        {kids.map((c, i) => (
          <NodeEditor key={i} node={c} known={known} refKey={refKey} depth={depth + 1}
            onChange={v => setKid(i, v)} onRemove={() => delKid(i)} />
        ))}
      </Paper>
    );
  }
  return (
    <LeafEditor node={node} known={known} refKey={refKey} onChange={onChange} onRemove={onRemove}
      onToGroup={() => onChange({ op: 'all', conditions: [node] })} />
  );
}

export { NodeEditor, OperandEditor, operandKind, CMP_OPTIONS, GROUP_OPS, PRICE_FIELDS };
export default NodeEditor;
